import React from 'react';
import { formatString } from '@deadpan/shared';
import type { Player } from '@deadpan/shared';
import { useTranslations } from '../../hooks/useTranslations';

export const STAND_APART_CARD_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'] as const;

const CARD_STYLES = [
  'from-rose-500 to-red-600 border-red-300',
  'from-amber-400 to-orange-500 border-amber-300',
  'from-emerald-500 to-teal-600 border-emerald-300',
  'from-sky-500 to-blue-600 border-sky-300',
  'from-violet-500 to-purple-700 border-violet-300',
  'from-fuchsia-500 to-pink-600 border-pink-300',
];

interface StandApartCardsProps {
  cardCount: number;
  selectedIndex: number | null;
  submitted: boolean;
  revealed: boolean;
  picks?: Record<string, number>;
  uniqueCardIndexes?: number[];
  chooserId?: string;
  playersById: Record<string, Player>;
  disabled?: boolean;
  onSelect: (cardIndex: number) => void;
}

export const StandApartCards: React.FC<StandApartCardsProps> = ({
  cardCount,
  selectedIndex,
  submitted,
  revealed,
  picks,
  uniqueCardIndexes = [],
  chooserId,
  playersById,
  disabled,
  onSelect,
}) => {
  const t = useTranslations();
  const sa = t.games.standApart;
  const uniqueSet = new Set(uniqueCardIndexes);

  const pickersForCard = (cardIndex: number): Player[] => {
    if (!picks) return [];
    return Object.entries(picks)
      .filter(([, index]) => index === cardIndex)
      .map(([playerId]) => playersById[playerId])
      .filter((player): player is Player => Boolean(player));
  };

  return (
    <div className="w-full grid grid-cols-2 gap-2 sm:gap-3">
      {Array.from({ length: cardCount }, (_, cardIndex) => {
        const letter = STAND_APART_CARD_LETTERS[cardIndex] ?? String(cardIndex + 1);
        const isSelected = selectedIndex === cardIndex;
        const isUnique = uniqueSet.has(cardIndex);
        const pickers = pickersForCard(cardIndex);
        const hasChooser = Boolean(chooserId && pickers.some((p) => p.id === chooserId));
        const style = CARD_STYLES[cardIndex] ?? CARD_STYLES[0];

        return (
          <button
            key={cardIndex}
            type="button"
            disabled={disabled || submitted || revealed}
            onClick={() => onSelect(cardIndex)}
            className={`relative min-h-[7.5rem] sm:min-h-[8.5rem] rounded-2xl border-2 text-left transition-transform disabled:opacity-100 ${
              isSelected && !revealed ? 'ring-4 ring-indigo-400 scale-[1.02]' : ''
            } ${revealed && isUnique ? 'ring-4 ring-amber-400' : ''} ${
              revealed && hasChooser ? 'ring-4 ring-pink-500' : ''
            } ${disabled || submitted || revealed ? 'cursor-default' : 'cursor-pointer active:scale-[0.98]'}`}
            aria-label={formatString(sa.cardLabel, { letter })}
          >
            <div className="h-full w-full [perspective:1000px]">
              <div
                className={`relative h-full min-h-[7.5rem] sm:min-h-[8.5rem] w-full transition-transform duration-700 [transform-style:preserve-3d] ${
                  revealed ? '[transform:rotateY(180deg)]' : ''
                }`}
              >
                <div
                  className={`absolute inset-0 flex flex-col items-center justify-center rounded-2xl border-2 bg-gradient-to-br text-white [backface-visibility:hidden] ${style}`}
                  aria-hidden={revealed}
                >
                  <span className="text-[clamp(1.5rem,8vw,3rem)] font-black leading-none">{letter}</span>
                </div>
                <div
                  className={`absolute inset-0 flex flex-col rounded-2xl border-2 bg-white p-2 sm:p-3 [transform:rotateY(180deg)] [backface-visibility:hidden] ${
                    isUnique ? 'border-amber-400 bg-amber-50' : 'border-gray-200'
                  }`}
                  aria-hidden={!revealed}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-lg sm:text-xl font-black text-gray-800">{letter}</span>
                    {isUnique && (
                      <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wide text-amber-700 bg-amber-200 rounded-full px-2 py-0.5">
                        {sa.uniqueBadge}
                      </span>
                    )}
                  </div>
                  {pickers.length === 0 ? (
                    <p className="text-xs sm:text-sm text-gray-500">{sa.noPickers}</p>
                  ) : (
                    <ul className="space-y-0.5 overflow-hidden">
                      {pickers.map((player) => (
                        <li
                          key={player.id}
                          className={`text-xs sm:text-sm font-semibold truncate ${
                            player.id === chooserId ? 'text-pink-700' : 'text-gray-800'
                          }`}
                        >
                          {player.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};
